import { useEffect, useRef } from "react";
import "../boq-editor.scss";
import { useTranslation } from "react-i18next";
import { faTriangleExclamation, faCircleInfo, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const SEVERITY_META = {
    Information: { icon: faCircleInfo, color: "#d97706" },
    Warning: { icon: faTriangleExclamation, color: "#d97706" },
    Error: { icon: faTriangleExclamation, color: "#dc2626" },
    Critical: { icon: faTriangleExclamation, color: "#dc2626" },
};

const SeverityIcon = ({ severity }) => {
    const meta = SEVERITY_META[severity] ?? SEVERITY_META.Information;
    const isError = severity === "Error" || severity === "Critical";
    return (
        <FontAwesomeIcon
            icon={meta.icon}
            className={`boq-severity-fa-icon boq-severity-fa-icon--${isError ? "danger" : "warn"}`}
        />
    );
};

/**
 * Cosuno-style floating validation panel, rendered as a positioned dropdown
 * anchored below the "Formale Prüfung" / validation badge button.
 *
 * Props:
 *  - result        ValidationResult from Redux (null = hide)
 *  - onClose       () => void  - called when user clicks × or presses Escape
 *  - onElementClick (elementId) => void - navigate to element in grid
 */
const BoQValidationPanel = ({ result, onClose, onElementClick }) => {
    const { t } = useTranslation();
    const panelRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        if (!result) return;
        const handler = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                // Also ignore clicks on the badge button itself (it toggles).
                onClose?.();
            }
        };
        // Use capture so clicks on grid rows that also trigger edit mode are handled first.
        document.addEventListener("mousedown", handler, true);
        return () => document.removeEventListener("mousedown", handler, true);
    }, [result, onClose]);

    // Close on Escape
    useEffect(() => {
        if (!result) return;
        const handler = (e) => { if (e.key === "Escape") onClose?.(); };
        document.addEventListener("keydown", handler);
        return () => document.removeEventListener("keydown", handler);
    }, [result, onClose]);

    if (!result) return null;

    // Group by (severity + message), collecting elementIds.
    const grouped = [];
    const seen = new Map();
    for (const cr of result.checkResults ?? []) {
        const key = `${cr.severity}||${cr.message}`;
        const elementId = cr.projectValidationCheckDetails?.elementId ?? null;
        if (seen.has(key)) {
            const g = grouped[seen.get(key)];
            g.count++;
            if (elementId && !g.elementIds.includes(elementId)) g.elementIds.push(elementId);
        } else {
            seen.set(key, grouped.length);
            grouped.push({ severity: cr.severity, message: cr.message, count: 1, elementIds: elementId ? [elementId] : [] });
        }
    }

    const isValid = result.isSchemaCompliant;

    return (
        <div className="boq-validation-panel" ref={panelRef} role="dialog" aria-label={t("boq.validate.title")}>
            {/* Header */}
            <div className="boq-validation-panel__header">
                <span className="boq-validation-panel__title">{t("boq.validate.title")}</span>
                <button
                    type="button"
                    className="boq-validation-panel__close"
                    onClick={onClose}
                    aria-label={t("common.close")}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>
            </div>

            {/* Hint */}
            <p className="boq-validation-panel__hint">{t("boq.validate.gaeb-standard-hint")}</p>

            {isValid ? (
                <div className="boq-validation-panel__valid">
                    {t("boq.validate.valid")}
                </div>
            ) : (
                <div className="boq-validation-panel__list">
                    {grouped.map((g, i) => {
                        const isClickable = g.elementIds.length > 0 && !!onElementClick;
                        return (
                            <div
                                key={i}
                                className={`boq-validation-panel__item${isClickable ? " boq-validation-panel__item--clickable" : ""}`}
                                role={isClickable ? "button" : undefined}
                                tabIndex={isClickable ? 0 : undefined}
                                onClick={isClickable ? () => { onElementClick(g.elementIds[0]); onClose?.(); } : undefined}
                                onKeyDown={
                                    isClickable
                                        ? (e) => {
                                            if (e.key === "Enter" || e.key === " ") {
                                                e.preventDefault();
                                                onElementClick(g.elementIds[0]);
                                                onClose?.();
                                            }
                                        }
                                        : undefined
                                }
                            >
                                <SeverityIcon severity={g.severity} />
                                <span className="boq-validation-panel__item-text">{g.message}</span>
                                <span className="boq-validation-panel__item-count">{g.count}</span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default BoQValidationPanel;
